import React from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import StepHeader from './StepHeader';
import '../common.css';

// Tour scale options
const tourScaleOptions = [
  { id: "small", label: "Small (Clubs & Small Venues)" },
  { id: "medium", label: "Medium (Theater & Mid-Size Venues)" },
  { id: "large", label: "Large (Arenas & Amphitheaters)" },
  { id: "stadium", label: "Stadium/Festival" },
  { id: "mixed", label: "Mixed Scale" }
];

// Tour objectives options
const objectiveOptions = [
  { id: "promote-album", label: "Promote New Album/Release" },
  { id: "market-development", label: "Market Development" },
  { id: "revenue", label: "Maximize Revenue" },
  { id: "fanbase", label: "Grow Fanbase" },
  { id: "branding", label: "Branding & Visibility" },
  { id: "test-market", label: "Test New Markets" }
];

const TourObjectives = ({ formData, onInputChange, onNext, error }) => {
  // Handle checkbox changes for multi-select options
  const handleMultiSelectChange = (field, value, isChecked) => {
    const currentValues = formData[field] || [];
    let newValues;
    
    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(item => item !== value);
    }
    
    onInputChange({ [field]: newValues });
  };

  // Handle input changes for text fields
  const handleInputChange = (field, value) => {
    onInputChange({ [field]: value });
  };

  // Check if a value is selected in a multi-select list
  const isSelected = (field, value) => {
    return (formData[field] || []).includes(value);
  };

  // Form validation 
  const validateForm = () => {
    // Basic validation - could be expanded
    if (!formData.tourName || !formData.artistName) {
      return false;
    }
    return true;
  };

  // Handle the form submission
  const handleContinue = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom">
        <StepHeader title="Plan Your Tour Concept" />
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        
        <Form>
          {/* Basic Tour Information */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Basic Tour Information</h5>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Tour Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    className="dark-input"
                    value={formData.tourName || ''}
                    onChange={(e) => handleInputChange('tourName', e.target.value)}
                    placeholder="Enter tour name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Artist/Band Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    className="dark-input"
                    value={formData.artistName || ''}
                    onChange={(e) => handleInputChange('artistName', e.target.value)}
                    placeholder="Enter artist name"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label className="text-white">Genre</Form.Label>
                  <Form.Control
                    type="text"
                    className="dark-input"
                    value={formData.genre || ''}
                    onChange={(e) => handleInputChange('genre', e.target.value)}
                    placeholder="e.g. Rock, Pop, Hip-Hop, Electronic"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Tour Scale</Form.Label>
              <Row>
                {tourScaleOptions.map(scale => (
                  <Col md={6} key={scale.id} className="mb-2">
                    <Form.Check
                      type="radio"
                      name="tourScale"
                      id={`scale-${scale.id}`}
                      className="dark-checkbox"
                      label={scale.label}
                      checked={formData.tourScale === scale.id}
                      onChange={() => handleInputChange('tourScale', scale.id)}
                    />
                  </Col>
                ))}
              </Row>
            </Form.Group>
          </div>
          
          {/* Tour Description */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Tour Description</h5>
            
            <Form.Group className="mb-4">
              <Form.Label className="text-white">Tour Concept</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="dark-input"
                value={formData.tourConcept || ''}
                onChange={(e) => handleInputChange('tourConcept', e.target.value)}
                placeholder="Describe your tour concept, theme, or vision..."
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Primary Tour Objectives</Form.Label>
              <Row>
                {objectiveOptions.map(objective => (
                  <Col md={6} key={objective.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`objective-${objective.id}`}
                      className="dark-checkbox"
                      label={objective.label}
                      checked={isSelected('tourObjectives', objective.id)}
                      onChange={(e) => handleMultiSelectChange('tourObjectives', objective.id, e.target.checked)}
                    />
                  </Col>
                ))}
              </Row>
            </Form.Group>
          </div>
          
          {/* Target Audience */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Target Audience</h5>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Primary Age Range</Form.Label>
                  <Form.Select
                    className="dark-input"
                    value={formData.audienceAge || ''}
                    onChange={(e) => handleInputChange('audienceAge', e.target.value)}
                  >
                    <option value="">Select age range...</option>
                    <option value="13-17">13-17</option>
                    <option value="18-24">18-24</option>
                    <option value="25-34">25-34</option>
                    <option value="35-44">35-44</option>
                    <option value="45+">45+</option>
                    <option value="all-ages">All Ages</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Fanbase Size Estimation</Form.Label>
                  <Form.Select
                    className="dark-input"
                    value={formData.fanbaseSize || ''}
                    onChange={(e) => handleInputChange('fanbaseSize', e.target.value)}
                  >
                    <option value="">Select fanbase size...</option>
                    <option value="emerging">Emerging Artist (Under 10K fans)</option>
                    <option value="developing">Developing Artist (10K-50K fans)</option>
                    <option value="established">Established Artist (50K-250K fans)</option>
                    <option value="major">Major Artist (250K-1M fans)</option>
                    <option value="superstar">Superstar (1M+ fans)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Audience Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                className="dark-input"
                value={formData.audienceDescription || ''}
                onChange={(e) => handleInputChange('audienceDescription', e.target.value)}
                placeholder="Describe your target audience demographics, interests, behaviors..."
              />
            </Form.Group>
          </div>
          
          {/* Career Context */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Career Context</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Artist Career Stage</Form.Label>
              <Form.Select
                className="dark-input"
                value={formData.careerStage || ''}
                onChange={(e) => handleInputChange('careerStage', e.target.value)}
              >
                <option value="">Select career stage...</option>
                <option value="new">New Artist (First Tour)</option>
                <option value="emerging">Emerging (Building Reputation)</option>
                <option value="established">Established (Consistent Draw)</option>
                <option value="headliner">Headliner (Major Draw)</option>
                <option value="legacy">Legacy Act</option>
              </Form.Select>
            </Form.Group>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Recent Release Status</Form.Label>
                  <Form.Select
                    className="dark-input"
                    value={formData.releaseStatus || ''}
                    onChange={(e) => handleInputChange('releaseStatus', e.target.value)}
                  >
                    <option value="">Select release status...</option>
                    <option value="pre-release">Pre-Release Tour</option>
                    <option value="supporting-release">Supporting Recent Release</option>
                    <option value="no-release">No Recent Release</option>
                    <option value="compilation">Greatest Hits/Compilation</option>
                    <option value="anniversary">Anniversary Tour</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Previous Touring Experience</Form.Label>
                  <Form.Select
                    className="dark-input"
                    value={formData.tourExperience || ''}
                    onChange={(e) => handleInputChange('tourExperience', e.target.value)}
                  >
                    <option value="">Select touring experience...</option>
                    <option value="none">None (First Tour)</option>
                    <option value="local">Local Shows Only</option>
                    <option value="regional">Regional Touring</option>
                    <option value="national">National Touring</option>
                    <option value="international">International Touring</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>
          
          {/* Additional Notes */}
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Additional Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              value={formData.additionalNotes || ''}
              onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
              placeholder="Any other details about your tour concept or objectives..."
            />
          </Form.Group>
          
          {/* Required fields note */}
          <p className="text-muted small mb-4"><span className="text-danger">*</span> Required fields</p>
          
          <div className="d-flex justify-content-end">
            <Button
              className="dark-primary-button"
              onClick={handleContinue}
              disabled={!validateForm()}
            >
              Continue
              <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default TourObjectives;